import React, { useCallback } from 'react'
import { BaseExternalIntegrationSelect } from './BaseExternalIntegrationSelect'
import { Item, Section } from '@react-stately/collections'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { SelectedItem } from './SelectedItem'
import { ExternalIntegrationItem } from './ExternalIntegrationItem'
import Styles from './ExternalIntegrationSelect.module.scss'
import UserProfileAdd2 from 'blaze-ui/icons/20/UserProfileAdd2'
import { CollectionElement } from '@react-types/shared'
import { IconButton } from 'components/IconButton/IconButton'
import { useOpenIntegrations } from 'hooks/useOpenIntegrations'
import { Button } from 'blaze-ui/Button'
import { ButtonVariant } from 'blaze-ui/Button/Types'

export interface ExternalIntegrationSelectProps {
  externalIntegrations: ExternalIntegration[]
  selectedExternalIntegrationId?: number | null
  onChange: (externalIntegrationId: number) => void
  variant?: 'condensed' | 'default' | 'ghost'
  readOnly?: boolean
  label?: string
  onOpenIntegrations?: () => void
  maxWidth?: number
}

export const ExternalIntegrationSelect: React.FC<ExternalIntegrationSelectProps> = ({
  externalIntegrations,
  selectedExternalIntegrationId,
  onChange,
  variant = 'default',
  readOnly,
  label,
  onOpenIntegrations,
  maxWidth,
}) => {
  const openIntegrations = useOpenIntegrations()
  const onConnectIntegration = useCallback(() => {
    openIntegrations()
    onOpenIntegrations?.()
  }, [openIntegrations, onOpenIntegrations])

  if (externalIntegrations.length === 0) {
    if (label)
      return (
        <Button
          size="xs"
          variant={(variant as ButtonVariant) || 'tertiary'}
          frontIcon={UserProfileAdd2}
          onPress={onConnectIntegration}
        >
          {label}
        </Button>
      )

    return (
      <IconButton onPress={onConnectIntegration} data-testid="external-integration-select-button">
        <UserProfileAdd2 size={24} />
      </IconButton>
    )
  }

  return (
    <BaseExternalIntegrationSelect
      data-testid="external-integration-select"
      selectedKey={selectedExternalIntegrationId?.toString() ?? undefined}
      onSelectionChange={(key) => {
        key === 'open-integrations' ? openIntegrations() : onChange(parseInt(key as string, 10))
      }}
      aria-label={'Select integration'}
      variant={variant}
      readOnly={readOnly}
      renderSelectValue={(key) => {
        if (!key) return null
        const selectedExternalIntegration = externalIntegrations.find(
          (externalIntegration) => externalIntegration.id.toString() === key,
        )

        if (!selectedExternalIntegration) return null

        return <SelectedItem item={selectedExternalIntegration} variant={variant} maxWidth={maxWidth} />
      }}
    >
      {
        externalIntegrations.map((externalIntegration) => (
          <Item key={externalIntegration.id.toString()} textValue={externalIntegration.provider}>
            <ExternalIntegrationItem externalIntegration={externalIntegration} />
          </Item>
        )) as unknown as CollectionElement<object>
      }

      <Section>
        <Item key="open-integrations" textValue="Manage Integrations">
          <div className={Styles.connectAccountButton}>
            <UserProfileAdd2 />
            Connect New Account
          </div>
        </Item>
      </Section>
    </BaseExternalIntegrationSelect>
  )
}
